import { FC, Fragment, ReactElement, useMemo } from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer, Observer } from 'mobx-react';

import { AggregateResult, GroupResult } from '@progress/kendo-data-query';
import { GridCellProps, GridHeaderCellProps } from '@progress/kendo-react-grid';

import {
    BodyText,
    Button,
    Checkbox,
    Headline,
    Stack,
    Table,
    TableColumn,
    TableNoRecords,
} from '@servicetitan/design-system';

import { ApiGroup, ApiScope, ApiScopeGroup } from '../../api/app.api';
import { createSearchRegExp, SearchTextHighlighter } from '../../common';
import { getDisplayNameString } from '../utils';
import { SearchIcon } from './icons';

class ScopeGridItem {
    constructor(
        private readonly scopeGroup: ApiScopeGroup,
        private readonly scope: ApiScope,
        private readonly enabledAuthScopes: Set<string>,
        private readonly selectedAuthScopes: Set<string>,
    ) {}

    get scopeGroupDisplayName(): string {
        return this.scopeGroup.displayName;
    }

    get scopeDisplayName(): string {
        return getDisplayNameString(this.scope);
    }

    get canRead(): boolean {
        return this.enabledAuthScopes.has(this.scope.name + ':r');
    }

    get canWrite(): boolean {
        return this.enabledAuthScopes.has(this.scope.name + ':w');
    }

    get isReadSelected(): boolean | null {
        return this.scope.hasRead ? this.isSelected(':r') : null;
    }

    set isReadSelected(value: boolean | null) {
        this.select(':r', value);
    }

    get isWriteSelected(): boolean | null {
        return this.scope.hasWrite ? this.isSelected(':w') : null;
    }

    set isWriteSelected(value: boolean | null) {
        this.select(':w', value);
    }

    private isSelected(access: ':r' | ':w'): boolean {
        return this.selectedAuthScopes.has(this.scope.name + access);
    }

    private select(access: ':r' | ':w', value: boolean | null) {
        if (value !== null) {
            this.selectedAuthScopes[value ? 'add' : 'delete'](this.scope.name + access);
        }
    }
}

class ScopeGroupGridItem implements GroupResult {
    readonly items: ScopeGridItem[];
    readonly hasEnabledScopes: boolean;
    @observable expanded = true;

    constructor(
        private readonly scopeGroup: ApiScopeGroup,
        private readonly scopes: ApiScope[], // the subset of scopeGroup.scopes to display
        private readonly enabledAuthScopes: Set<string>,
        private readonly selectedAuthScopes: Set<string>,
    ) {
        this.items = scopes.map(
            scope => new ScopeGridItem(scopeGroup, scope, enabledAuthScopes, selectedAuthScopes),
        );
        this.hasEnabledScopes = this.items.some(item => item.canRead || item.canWrite);
        makeObservable(this);
    }

    get aggregates(): AggregateResult {
        return {};
    }

    get field(): string {
        return 'scopeGroupDisplayName'; // name of ScopeGridItem property
    }

    get value(): string {
        return this.scopeGroup.displayName;
    }

    @action.bound
    handleSelectAll(): void {
        this.selectAll(true);
    }

    @action.bound
    handleSelectNone(): void {
        this.selectAll(false);
    }

    private selectAll(value: boolean) {
        const method = value ? 'add' : 'delete';
        this.scopes.forEach(scope => {
            if (this.enabledAuthScopes.has(scope.name + ':r')) {
                this.selectedAuthScopes[method](scope.name + ':r');
            }
            if (this.enabledAuthScopes.has(scope.name + ':w')) {
                this.selectedAuthScopes[method](scope.name + ':w');
            }
        });
    }
}

const CenteredHeaderCell: FC<GridHeaderCellProps> = ({ title }) => (
    <span className="k-cell-inner justify-content-center">
        <span className="k-link">
            <span className="k-column-title">{title}</span>
        </span>
    </span>
);
CenteredHeaderCell.displayName = 'CenteredHeaderCell';

interface ScopeGroupHeaderCellProps {
    columnsCount?: number;
    dataItem: ScopeGroupGridItem;
    expanded?: boolean;
    globalSearchRegExp: RegExp | null;
}

const ScopeGroupHeaderCell: FC<ScopeGroupHeaderCellProps> = ({
    columnsCount,
    dataItem,
    expanded,
    globalSearchRegExp,
}) => {
    return (
        <Fragment>
            <td className="p-x-0-i ta-center-i">
                <Button
                    xsmall
                    fill="subtle"
                    iconName={expanded ? 'chevron_right' : 'expand_more'}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    onClick={() => {
                        runInAction(() => {
                            dataItem.expanded = !dataItem.expanded;
                        });
                    }}
                />
            </td>
            <td colSpan={columnsCount! - 1}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Stack.Item>
                        <Headline size="small" className="m-b-half">
                            <SearchTextHighlighter
                                content={dataItem.value}
                                globalSearchRegExp={globalSearchRegExp}
                            />
                        </Headline>
                    </Stack.Item>
                    <Stack.Item>
                        <Button
                            primary
                            fill="subtle"
                            xsmall
                            disabled={!dataItem.hasEnabledScopes}
                            onClick={dataItem.handleSelectAll}
                        >
                            Select All
                        </Button>
                        <Button
                            primary
                            fill="subtle"
                            xsmall
                            disabled={!dataItem.hasEnabledScopes}
                            onClick={dataItem.handleSelectNone}
                        >
                            Select None
                        </Button>
                    </Stack.Item>
                </Stack>
            </td>
        </Fragment>
    );
};
ScopeGroupHeaderCell.displayName = 'ScopeGroupHeaderCell';

interface CheckboxDataCellProps {
    dataItem: ScopeGridItem;
    enabledField: 'canRead' | 'canWrite'; // names of ScopeGridItem properties
    checkedField: 'isReadSelected' | 'isWriteSelected'; // names of ScopeGridItem properties
}

const CheckboxDataCell: FC<CheckboxDataCellProps> = ({ dataItem, enabledField, checkedField }) => {
    return (
        <td className="ta-center-i">
            <Observer>
                {() => {
                    const checked = dataItem[checkedField];
                    return checked !== null ? (
                        <Checkbox
                            disabled={!dataItem[enabledField]}
                            checked={checked}
                            onChange={(_value, checked: boolean) => {
                                runInAction(() => {
                                    dataItem[checkedField] = checked;
                                });
                            }}
                        />
                    ) : null;
                }}
            </Observer>
        </td>
    );
};
CheckboxDataCell.displayName = 'CheckboxDataCell';

const CheckboxCell: FC<GridCellProps> = ({ dataItem, field, rowType }) => {
    return rowType === 'data' ? (
        <CheckboxDataCell
            dataItem={dataItem as ScopeGridItem}
            enabledField={field === 'isReadSelected' ? 'canRead' : 'canWrite'}
            checkedField={field as 'isReadSelected' | 'isWriteSelected'}
        />
    ) : null;
};
CheckboxCell.displayName = 'CheckboxCell';

export interface ApiScopesTableProps {
    apiGroup: ApiGroup;
    enabledAuthScopes: Set<string>;
    selectedAuthScopes: Set<string>;
    searchText: string;
}

export const ApiScopeTable: FC<ApiScopesTableProps> = observer(
    ({ apiGroup, enabledAuthScopes, selectedAuthScopes, searchText }) => {
        const globalSearchRegExp = useMemo(
            () => createSearchRegExp(searchText, 'gi'),
            [searchText],
        );

        const SearchTextHighlighterCell = useMemo((): FC<GridCellProps> => {
            return ({ dataItem, field, rowType }) =>
                rowType === 'data' ? (
                    <td>
                        <SearchTextHighlighter
                            content={dataItem[field!]}
                            globalSearchRegExp={globalSearchRegExp}
                        />
                    </td>
                ) : null;
        }, [globalSearchRegExp]);

        const cellRender = useMemo(
            () =>
                (
                    tdElement: ReactElement<HTMLTableCellElement> | null,
                    { columnIndex, columnsCount, dataItem, expanded, rowType }: GridCellProps,
                ) => {
                    return rowType === 'groupHeader' && columnIndex === 0 ? (
                        <ScopeGroupHeaderCell
                            columnsCount={columnsCount}
                            dataItem={dataItem}
                            expanded={expanded}
                            globalSearchRegExp={globalSearchRegExp}
                        />
                    ) : (
                        tdElement
                    );
                },
            [globalSearchRegExp],
        );

        const scopeGroupGridItems = useMemo(() => {
            const searchRegExp = createSearchRegExp(searchText, 'i'); // must not have the 'g' flag
            return apiGroup.scopeGroups
                .filter(
                    scopeGroup =>
                        !searchRegExp ||
                        searchRegExp.test(scopeGroup.displayName) ||
                        scopeGroup.scopes.some(scope => searchRegExp.test(scope.displayName)),
                )
                .map(
                    scopeGroup =>
                        new ScopeGroupGridItem(
                            scopeGroup,
                            !searchRegExp || searchRegExp.test(scopeGroup.displayName)
                                ? scopeGroup.scopes
                                : scopeGroup.scopes.filter(scope =>
                                      searchRegExp.test(scope.displayName),
                                  ),
                            enabledAuthScopes,
                            selectedAuthScopes,
                        ),
                );
        }, [apiGroup, enabledAuthScopes, selectedAuthScopes, searchText]);

        /*
         * Because <Table> doesn't support MobX, we must read the value of every ScopeGroupGridItem.expanded property
         * ourselves in order for the table to be re-rendered when a group is expanded or collapsed:
         */
        scopeGroupGridItems.map(item => item.expanded); // Discard the result.

        return (
            <Table
                data={scopeGroupGridItems}
                group={[{ field: 'scopeGroupDisplayName' }]}
                cellRender={cellRender}
                striped={false}
                expandField="expanded"
                style={{ height: '100%' }}
            >
                <TableNoRecords>
                    <Stack direction="column" alignItems="center" spacing={2} className="m-y-2">
                        {SearchIcon}
                        <Stack.Item className="ta-center">
                            <Headline size="small" className="m-b-1">
                                No matching scopes found
                            </Headline>
                            <BodyText size="small" subdued>
                                Please adjust your search and try again.
                            </BodyText>
                        </Stack.Item>
                    </Stack>
                </TableNoRecords>
                <TableColumn
                    field="scopeDisplayName"
                    title="API Scope"
                    cell={SearchTextHighlighterCell}
                />
                <TableColumn
                    field="isReadSelected"
                    title="Read"
                    width="100px"
                    headerCell={CenteredHeaderCell}
                    cell={CheckboxCell}
                />
                <TableColumn
                    field="isWriteSelected"
                    title="Write"
                    width="100px"
                    headerCell={CenteredHeaderCell}
                    cell={CheckboxCell}
                />
            </Table>
        );
    },
);
ApiScopeTable.displayName = 'ApiScopeTable';
